import {
  getFirestore,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  collection,
  query,
  where,
  orderBy,
  startAt,
  endAt,
  getDocs,
  serverTimestamp,
  GeoPoint,
  Timestamp
} from 'firebase/firestore'
import { geohashForLocation, geohashQueryBounds } from 'geofire-common'
import Database from '../types/Database'
import { filterByRadius } from '../utils/distanceUtils'
import { useTestDataStore } from '../stores/testDataStore'

// --- Error types ---

export type DiveServiceErrorCode =
  | 'VALIDATION_ERROR'
  | 'PERMISSION_ERROR'
  | 'NETWORK_ERROR'
  | 'ACCREDITATION_REQUIRED'
  | 'GUIDED_FLAG_REQUIRED'

export class DiveServiceError extends Error {
  constructor(public code: DiveServiceErrorCode, message: string) {
    super(message)
    this.name = 'DiveServiceError'
  }
}

// --- Status lifecycle ---

const TERMINAL_STATUSES = ['cancelled', 'completed'] as const
const VALID_STATUSES = ['open', 'full', 'cancelled', 'completed'] as const
const VALID_DEPTH_TIERS = ['18m', '30m', '30m+'] as const

export function canTransition(
  currentStatus: string,
  newStatus: string
): boolean {
  if (!VALID_STATUSES.includes(newStatus as (typeof VALID_STATUSES)[number]))
    return false
  if (
    TERMINAL_STATUSES.includes(
      currentStatus as (typeof TERMINAL_STATUSES)[number]
    )
  )
    return false
  return true
}

// --- Validation ---

function validateDiveFields(
  data: Database.DiveFormInputs,
  guidedDiveFlag: boolean
): void {
  if (
    !data.siteName ||
    data.siteName.length < 1 ||
    data.siteName.length > 100
  ) {
    throw new DiveServiceError(
      'VALIDATION_ERROR',
      'siteName must be between 1 and 100 characters'
    )
  }

  if (
    !data.description ||
    data.description.length < 1 ||
    data.description.length > 500
  ) {
    throw new DiveServiceError(
      'VALIDATION_ERROR',
      'description must be between 1 and 500 characters'
    )
  }

  if (!data.duration || data.duration < 15 || data.duration > 600) {
    throw new DiveServiceError(
      'VALIDATION_ERROR',
      'duration must be between 15 and 600 minutes'
    )
  }

  if (
    !data.depthTier ||
    !VALID_DEPTH_TIERS.includes(
      data.depthTier as (typeof VALID_DEPTH_TIERS)[number]
    )
  ) {
    throw new DiveServiceError(
      'VALIDATION_ERROR',
      'depthTier must be one of 18m, 30m, or 30m+'
    )
  }

  if (!data.dateTime) {
    throw new DiveServiceError('VALIDATION_ERROR', 'dateTime is required')
  }

  const dateTimeValue = new Date(data.dateTime)
  if (dateTimeValue <= new Date()) {
    throw new DiveServiceError(
      'VALIDATION_ERROR',
      'dateTime must be in the future'
    )
  }

  if (!data.location) {
    throw new DiveServiceError('VALIDATION_ERROR', 'location is required')
  }

  if (
    !data.meetingPoint ||
    data.meetingPoint.length < 1 ||
    data.meetingPoint.length > 200
  ) {
    throw new DiveServiceError(
      'VALIDATION_ERROR',
      'meetingPoint must be between 1 and 200 characters'
    )
  }

  if (data.notes && data.notes.length > 500) {
    throw new DiveServiceError(
      'VALIDATION_ERROR',
      'notes must be at most 500 characters'
    )
  }

  if (data.type === 'guided') {
    if (!data.price || data.price <= 0 || data.price > 10000) {
      throw new DiveServiceError(
        'VALIDATION_ERROR',
        'price must be greater than 0 and at most 10000 for guided dives'
      )
    }

    if (
      !data.maxParticipants ||
      data.maxParticipants < 1 ||
      data.maxParticipants > 10
    ) {
      throw new DiveServiceError(
        'VALIDATION_ERROR',
        'maxParticipants must be between 1 and 10 for guided dives'
      )
    }
  }
}

// --- Service functions ---

export async function createDive(
  data: Database.DiveFormInputs,
  userId: string,
  accreditationStatus: string,
  guidedDiveFlag: boolean
): Promise<string> {
  // Authorization checks
  if (accreditationStatus !== 'verified') {
    throw new DiveServiceError(
      'ACCREDITATION_REQUIRED',
      'Verified accreditation is required to create a dive'
    )
  }

  if (data.type === 'guided' && !guidedDiveFlag) {
    throw new DiveServiceError(
      'GUIDED_FLAG_REQUIRED',
      'Guided dive flag is required to create guided dives'
    )
  }

  // Field validation
  validateDiveFields(data, guidedDiveFlag)

  // Compute geohash from location
  const { lat, lng } = data.location!
  const geohash = geohashForLocation([lat, lng])

  // Build document
  const isBuddy = data.type === 'buddy'
  const diveDoc = {
    hostId: userId,
    type: data.type,
    siteName: data.siteName,
    description: data.description,
    location: new GeoPoint(lat, lng),
    geohash,
    dateTime: Timestamp.fromDate(new Date(data.dateTime)),
    duration: data.duration,
    depthTier: data.depthTier,
    price: isBuddy ? null : data.price!,
    maxParticipants: isBuddy ? 3 : data.maxParticipants!,
    meetingPoint: data.meetingPoint,
    notes: data.notes || null,
    status: 'open',
    pendingRequestCount: 0,
    testData: false,
    createdAt: serverTimestamp()
  }

  try {
    const db = getFirestore()
    const divesCollection = collection(db, 'dives')
    const docRef = await addDoc(divesCollection, diveDoc)
    return docRef.id
  } catch (error) {
    throw new DiveServiceError(
      'NETWORK_ERROR',
      'Failed to create dive. Please try again.'
    )
  }
}

export async function queryNearbyDives(
  center: { lat: number; lng: number },
  radiusKm: number,
  currentUserId: string
): Promise<
  (Database.Dive & { distanceKm: number; hostHandle: string | null })[]
> {
  const radiusMetres = radiusKm * 1000
  const bounds = geohashQueryBounds([center.lat, center.lng], radiusMetres)

  const db = getFirestore()
  const divesCollection = collection(db, 'dives')

  try {
    const snapshots = await Promise.all(
      bounds.map(bound => {
        const q = query(
          divesCollection,
          orderBy('geohash'),
          startAt(bound[0]),
          endAt(bound[1])
        )
        return getDocs(q)
      })
    )

    // Merge and deduplicate
    const seenIds = new Set<string>()
    const rawDives: Database.Dive[] = []

    for (const snapshot of snapshots) {
      for (const docSnap of snapshot.docs) {
        if (seenIds.has(docSnap.id)) continue
        seenIds.add(docSnap.id)
        rawDives.push({ id: docSnap.id, ...docSnap.data() } as Database.Dive)
      }
    }

    // Apply Haversine filter
    const withinRadius = filterByRadius(rawDives, center, radiusKm)

    // Filter: open status, future dateTime, exclude host's own dives,
    // and respect the testData session toggle — real users never see test dives
    // and test-data mode only shows test dives.
    const now = new Date()
    const showTestData = useTestDataStore.getState().showTestData
    const filtered = withinRadius.filter(dive => {
      if (dive.status !== 'open') return false
      if (dive.dateTime.toDate() <= now) return false
      if (dive.hostId === currentUserId) return false
      // testData field may be absent on legacy docs — treat absence as false
      const isTestDive = (dive as any).testData === true
      if (showTestData !== isTestDive) return false
      return true
    })

    // Sort by dateTime ascending
    filtered.sort(
      (a, b) => a.dateTime.toDate().getTime() - b.dateTime.toDate().getTime()
    )

    // Fetch host handles
    const uniqueHostIds = [...new Set(filtered.map(d => d.hostId))]
    const handleMap = new Map<string, string | null>()

    await Promise.all(
      uniqueHostIds.map(async uid => {
        try {
          const userRef = doc(db, 'users', uid)
          const userSnap = await getDoc(userRef)
          handleMap.set(
            uid,
            userSnap.exists() ? userSnap.data().handle ?? null : null
          )
        } catch {
          handleMap.set(uid, null)
        }
      })
    )

    return filtered.map(dive => ({
      ...dive,
      hostHandle: handleMap.get(dive.hostId) ?? null
    }))
  } catch (error) {
    throw new DiveServiceError(
      'NETWORK_ERROR',
      'Failed to query nearby dives. Please try again.'
    )
  }
}

export async function updateDiveStatus(
  diveId: string,
  newStatus: string,
  currentUserId: string
): Promise<void> {
  const db = getFirestore()
  const diveRef = doc(db, 'dives', diveId)

  try {
    const diveSnap = await getDoc(diveRef)

    if (!diveSnap.exists()) {
      throw new DiveServiceError('VALIDATION_ERROR', 'Dive not found')
    }

    const diveData = diveSnap.data() as Omit<Database.Dive, 'id'>

    // Verify host ownership
    if (diveData.hostId !== currentUserId) {
      throw new DiveServiceError(
        'PERMISSION_ERROR',
        'Only the host can update dive status'
      )
    }

    // Validate status transition
    if (!canTransition(diveData.status, newStatus)) {
      throw new DiveServiceError(
        'VALIDATION_ERROR',
        `Cannot transition from "${diveData.status}" to "${newStatus}"`
      )
    }

    await updateDoc(diveRef, { status: newStatus })
  } catch (error) {
    if (error instanceof DiveServiceError) throw error
    throw new DiveServiceError(
      'NETWORK_ERROR',
      'Failed to update dive status. Please try again.'
    )
  }
}

export async function getDiveById(
  diveId: string
): Promise<Database.Dive | null> {
  const db = getFirestore()
  const diveRef = doc(db, 'dives', diveId)

  try {
    const diveSnap = await getDoc(diveRef)

    if (!diveSnap.exists()) {
      return null
    }

    return { id: diveSnap.id, ...diveSnap.data() } as Database.Dive
  } catch (error) {
    throw new DiveServiceError(
      'NETWORK_ERROR',
      'Failed to fetch dive. Please try again.'
    )
  }
}

export async function getHostedDives(userId: string): Promise<Database.Dive[]> {
  const db = getFirestore()
  const showTestData = useTestDataStore.getState().showTestData
  const divesCollection = collection(db, 'dives')
  const q = query(
    divesCollection,
    where('hostId', '==', userId),
    where('testData', '==', showTestData)
  )
  const snap = await getDocs(q)
  const dives: Database.Dive[] = []
  snap.forEach(docSnap => {
    dives.push({ id: docSnap.id, ...docSnap.data() } as Database.Dive)
  })
  // Sort by dateTime descending (most recent first)
  dives.sort(
    (a, b) => b.dateTime.toDate().getTime() - a.dateTime.toDate().getTime()
  )
  return dives
}
